import { styled } from "@mui/material";
import SelectField from "controls/global/select-field";
import { SelectFieldOption } from "controls/global/select-field/SelectInput";
import { Jacket } from "entities/UIModel";
import React, { useEffect } from "react";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";

type Props = {
  schema: string;
  disabled: boolean;
  // The jacket's Trans Code is Collateral Mortgage.
  isCollateralMortgage: boolean;
  // ...and a primary loan is still selectable, so the control is worth showing.
  show: boolean;
  options: SelectFieldOption[];
  jackets: Jacket[] | undefined;
};

// The policy number (ProductReferenceID) of the jacket an option points at. Carried
// alongside the OrderID so the saved record names the loan policy.
const findPolicyNumber = (jackets: Jacket[] | undefined, orderID: any) =>
  (jackets || []).find((jacket) => String(jacket.orderID) === String(orderID))
    ?.productReferenceID ?? null;

// Full width of the jacket row (all 12 columns). The parent BoxFlexRow wraps, so
// a 100% basis puts this on its own line — the option text (jacket, form, status,
// date and policy number) needs the room.
const Container = styled("div")({
  flex: "0 0 100%",
  maxWidth: "100%",
  minWidth: 0,
  width: "100%",
});

const JacketPrimaryLoan = ({
  schema,
  disabled,
  isCollateralMortgage,
  show,
  options,
  jackets,
}: Props) => {
  const { getValues, setValue, clearErrors } = useFormWrapper();
  const fieldName = `${schema}.primaryLoanOrderID`;
  const policyNumberFieldName = `${schema}.primaryLoanPolicyNumber`;

  // The Trans Code moved away from Collateral Mortgage (or the jacket Type
  // changed), so a previously picked primary loan must not be left behind. Keyed on
  // the Trans Code rather than on `show`: merely hiding the control for want of
  // options must never wipe what an issued jacket already saved.
  useEffect(() => {
    if (isCollateralMortgage) return;
    if (!getValues(fieldName) && !getValues(policyNumberFieldName)) return;

    setValue(fieldName, null);
    setValue(policyNumberFieldName, null);
    clearErrors(fieldName);
  }, [
    isCollateralMortgage,
    fieldName,
    policyNumberFieldName,
    getValues,
    setValue,
    clearErrors,
  ]);

  // A lone primary loan is not a choice — pick it so issuing or revising a Collateral
  // Mortgage does not stall on a single-item dropdown. Only when the control is on screen
  // and editable, and never over a selection the jacket already has.
  const singleOptionValue = options.length === 1 ? options[0].value : undefined;

  useEffect(() => {
    if (!show || disabled || !singleOptionValue) return;
    if (getValues(fieldName)) return;

    // Mirrors a manual pick: SelectField writes the OrderID with shouldValidate, then
    // onChange writes the policy number.
    setValue(fieldName, singleOptionValue, { shouldValidate: true });
    setValue(policyNumberFieldName, findPolicyNumber(jackets, singleOptionValue));
  }, [
    show,
    disabled,
    singleOptionValue,
    jackets,
    fieldName,
    policyNumberFieldName,
    getValues,
    setValue,
  ]);

  // The option value is the selected jacket's orderID; carry its policy number
  // alongside it so the saved record names the loan policy.
  const handleChange = (_: any, selectedValue: any) => {
    setValue(policyNumberFieldName, findPolicyNumber(jackets, selectedValue));
  };

  if (!show) return null;

  return (
    <Container>
      <SelectField
        label="Attach to Primary Loan"
        name={fieldName}
        options={options}
        disabled={disabled}
        onChange={handleChange}
      />
    </Container>
  );
};

export default JacketPrimaryLoan;
